import { ArrowForward, Lock, Person, Visibility, VisibilityOff } from "@mui/icons-material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DialpadIcon from "@mui/icons-material/Dialpad";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthLayout, { fieldSx, ghostButtonSx, iconSx, submitButtonSx } from "./AuthLayout";
import { loginColors } from "./loginTheme";
import {
  clearPasswordUpdated,
  getPasswordUpdatedUsername,
  getRememberedUsername,
  setRememberedUsername,
  wasPasswordJustUpdated,
} from "../auth/loginPrefs";
import {
  clearPendingMfaToken,
  clearSession,
  getPendingMfaToken,
  restoreSession,
  setAccessToken,
  setPendingMfaToken,
  setSessionFromProfile,
} from "../auth/authSession";
import { passwordIsStrong } from "../utils/passwordRules";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 45;

const fieldInputProps = (icon) => ({
  startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
});

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const maskPhone = (value) => {
  const digits = digitsOnly(value);
  if (digits.length < 4) return "";
  return `••••${digits.slice(-4)}`;
};

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const remembered = getRememberedUsername();
  const updatedUser = getPasswordUpdatedUsername();
  const [username, setUsername] = useState(
    location.state?.username || updatedUser || remembered
  );
  const [password, setPassword] = useState("");
  const [rememberUser, setRememberUser] = useState(Boolean(remembered));
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [otp, setOtp] = useState("");
  const [phone, setPhone] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [step, setStep] = useState("credentials");
  const [notice, setNotice] = useState(() =>
    location.state?.passwordUpdated || wasPasswordJustUpdated()
      ? {
          severity: "success",
          message: "Password updated. Sign in with your new password.",
        }
      : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const otpRef = useRef(null);
  const phoneRef = useRef(null);
  const mobileOtpRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => () => clearPendingMfaToken(), []);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    const focusMap = {
      credentials: usernameRef,
      otp: otpRef,
      phone: phoneRef,
      phoneOtp: mobileOtpRef,
    };
    const node = focusMap[step]?.current;
    if (node) {
      node.focus();
      if (typeof node.select === "function" && node.value) node.select();
    }
  }, [step]);

  const startCooldown = () => setResendIn(RESEND_SECONDS);

  const readApiError = (data, fallback) =>
    data?.details || data?.message || fallback;

  const applyChallenge = (data) => {
    if (data?.token) setPendingMfaToken(data.token);
    if (data?.challengeId) setChallengeId(data.challengeId);
    const phoneHint =
      data?.maskedPhone ||
      data?.phone ||
      data?.mobile ||
      data?.users?.mobile ||
      data?.users?.phone;
    if (phoneHint) setMaskedPhone(maskPhone(phoneHint) || String(phoneHint));
  };

  const requestLoginChallenge = async ({ reset = false } = {}) => {
    if (reset) clearSession();
    const response = await fetch(`${apiUrl}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username: username.trim(), password }),
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  };

  const handleCredentials = async () => {
    const nextUser = username.trim();
    if (!nextUser || !password) {
      setNotice({
        severity: "error",
        message: "Enter both username and password.",
      });
      return;
    }

    setSubmitting(true);
    setNotice(null);
    clearPasswordUpdated();
    try {
      const { data } = await requestLoginChallenge({ reset: true });
      if (data.statusCode === 401 || data.status === 401) {
        setNotice({
          severity: "error",
          message: "Username or password is incorrect.",
        });
        return;
      }
      applyChallenge(data);

      if (data.statusCode === 200 && data.message === "REGISTER_PHONE") {
        setStep("phone");
        return;
      }
      if (data.statusCode === 200) {
        setStep("otp");
        startCooldown();
        return;
      }
      if (data.statusCode === 500) {
        setNotice({
          severity: "error",
          message: readApiError(data, "Could not start sign-in. Try again."),
        });
      } else {
        setNotice({
          severity: "error",
          message: readApiError(data, "Could not start sign-in. Try again."),
        });
      }
    } catch (err) {
      console.error("OTP Generate error:", err);
      setNotice({
        severity: "error",
        message: "We couldn’t send a verification code. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMobileSubmit = async () => {
    const nextPhone = digitsOnly(phone);
    if (!nextPhone) {
      setNotice({ severity: "error", message: "Enter a mobile number." });
      return;
    }
    if (nextPhone.length !== 10) {
      setNotice({
        severity: "error",
        message: "Mobile number must be exactly 10 digits.",
      });
      return;
    }

    setSubmitting(true);
    setNotice(null);
    try {
      const token = getPendingMfaToken();
      const response = await fetch(`${apiUrl}/api/register-phone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ phone: nextPhone }),
      });
      const data = await response.json().catch(() => ({}));
      if (data.statusCode === 200) {
        applyChallenge(data);
        setMaskedPhone(maskPhone(nextPhone));
        setStep("phoneOtp");
        startCooldown();
      } else {
        setNotice({
          severity: "error",
          message: readApiError(data, "Could not register this mobile number."),
        });
      }
    } catch (err) {
      console.error("OTP Generate error:", err);
      setNotice({
        severity: "error",
        message: "We couldn’t send a verification code. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const finishSignIn = async (data) => {
    clearPendingMfaToken();
    if (data.token) setAccessToken(data.token);

    const strongPassword = passwordIsStrong(password, username.trim(), data.role);
    const expired =
      data.passwordExpired === true || data.message === "PASSWORD_EXPIRED";

    setSessionFromProfile({
      ...data,
      isStrongPassword: strongPassword,
      passwordExpired: expired,
    });

    if (rememberUser) setRememberedUsername(username.trim());
    else setRememberedUsername("");

    if (expired || !strongPassword) {
      navigate("/change-password", {
        replace: true,
        state: { reason: expired ? "expired" : "weak" },
      });
      return;
    }

    await restoreSession();
    navigate("/company");
  };

  const handleMobileOtpSubmit = async () => {
    const code = digitsOnly(mobileOtp);
    if (!code) {
      setNotice({ severity: "error", message: "Enter the one-time code." });
      return;
    }

    setSubmitting(true);
    setNotice(null);
    try {
      const token = getPendingMfaToken();
      const response = await fetch(`${apiUrl}/api/verify-phone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ mobileOtp: code, challengeId }),
      });
      const data = await response.json().catch(() => ({}));

      if (data.statusCode === 200) {
        applyChallenge(data);
        if (data.challengeId || data.token) {
          setMobileOtp("");
          setStep("otp");
          startCooldown();
          setNotice({
            severity: "success",
            message: "Phone verified. Enter the sign-in code sent to your phone.",
          });
          return;
        }
        setNotice({
          severity: "success",
          message: "Phone verified. Continue to sign in.",
        });
        setStep("credentials");
        setMobileOtp("");
        return;
      }
      setNotice({
        severity: "error",
        message: readApiError(data, "That code didn’t match. Try again."),
      });
    } catch (err) {
      console.error("Verification Error:", err);
      setNotice({
        severity: "error",
        message: "We couldn’t verify that code. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async () => {
    const code = digitsOnly(otp);
    if (!code) {
      setNotice({ severity: "error", message: "Enter the one-time code." });
      return;
    }

    setSubmitting(true);
    setNotice(null);
    try {
      const token = getPendingMfaToken();
      const response = await fetch(`${apiUrl}/api/verify-mfa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ challengeId, otp: code }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.statusCode === 401) {
        setNotice({
          severity: "error",
          message: readApiError(
            data,
            "Verification failed. Check the code and try again."
          ),
        });
        return;
      }

      await finishSignIn(data);
    } catch (err) {
      console.error("Sign-in error:", err);
      setNotice({
        severity: "error",
        message: "Something went wrong while signing in. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetToCredentials = () => {
    clearPendingMfaToken();
    setStep("credentials");
    setOtp("");
    setPhone("");
    setMobileOtp("");
    setChallengeId("");
    setNotice(null);
    setResendIn(0);
  };

  const handleResend = async () => {
    if (resendIn > 0 || submitting) return;
    if (!challengeId) {
      setNotice({
        severity: "error",
        message: "Could not resend the code. Go back and sign in again.",
      });
      return;
    }

    const path = step === "phoneOtp" ? "/api/resend-phone" : "/api/resend-mfa";
    setSubmitting(true);
    setNotice(null);
    try {
      const token = getPendingMfaToken();
      const response = await fetch(`${apiUrl}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ challengeId }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.statusCode === 200) {
        applyChallenge(data);
        startCooldown();
        setNotice({
          severity: "success",
          message: "A new code was sent.",
        });
        return;
      }
      setNotice({
        severity: "error",
        message: readApiError(data, "Could not resend the code."),
      });
    } catch (err) {
      console.error("Resend error:", err);
      setNotice({
        severity: "error",
        message: "Could not resend the code. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (step === "credentials") handleCredentials();
    else if (step === "phone") handleMobileSubmit();
    else if (step === "phoneOtp") handleMobileOtpSubmit();
    else handleLogin();
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape" && step !== "credentials") {
      event.preventDefault();
      resetToCredentials();
    }
  };

  const copy = useMemo(() => {
    if (step === "phone") {
      return {
        heading: "Register your phone",
        helper: "This number will be used for one-time verification codes.",
        action: "Send code",
        pending: "Sending…",
      };
    }
    if (step === "phoneOtp") {
      return {
        heading: "Verify phone",
        helper: maskedPhone
          ? `Enter the code sent to ${maskedPhone}.`
          : "Enter the code sent to your phone.",
        action: "Verify phone",
        pending: "Verifying…",
      };
    }
    if (step === "otp") {
      return {
        heading: "Verify it’s you",
        helper: maskedPhone
          ? `Enter the one-time code sent to ${maskedPhone}.`
          : "Enter the one-time code to finish signing in.",
        action: "Sign in",
        pending: "Signing in…",
      };
    }
    return {
      heading: "Welcome back",
      helper: "Sign in using your company credentials.",
      action: "Sign in",
      pending: "Signing in…",
    };
  }, [maskedPhone, step]);

  return (
    <AuthLayout
      documentTitle="Sign in · Procurement"
      heading={copy.heading}
      helper={copy.helper}
    >
      <form onSubmit={onSubmit} onKeyDown={onKeyDown} noValidate>
        {notice ? (
          <Alert severity={notice.severity} role="alert" sx={{ mb: 2 }}>
            {notice.message}
          </Alert>
        ) : null}

        {step === "credentials" && (
          <>
            <TextField
              id="login-username"
              inputRef={usernameRef}
              placeholder="Username"
              hiddenLabel
              variant="outlined"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              inputProps={{ "aria-label": "Username" }}
              sx={fieldSx}
              InputProps={fieldInputProps(<Person sx={{ ...iconSx, color: loginColors.navy }} />)}
            />
            <TextField
              id="login-password"
              inputRef={passwordRef}
              placeholder="Password"
              hiddenLabel
              variant="outlined"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={(e) => setCapsLock(e.getModifierState("CapsLock"))}
              onKeyDown={(e) => setCapsLock(e.getModifierState("CapsLock"))}
              autoComplete="current-password"
              inputProps={{ "aria-label": "Password" }}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={iconSx} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((open) => !open)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={iconSx} />
                      ) : (
                        <Visibility sx={iconSx} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {capsLock ? (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1.5 }}>
                Caps Lock is on
              </Typography>
            ) : null}
            <FormControlLabel
              sx={{ mb: 1.5, ml: -0.5, color: loginColors.muted }}
              control={
                <Checkbox
                  size="small"
                  checked={rememberUser}
                  onChange={(e) => setRememberUser(e.target.checked)}
                  sx={{
                    color: loginColors.muted,
                    "&.Mui-checked": { color: loginColors.teal },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: loginColors.muted }}>
                  Remember username
                </Typography>
              }
            />
          </>
        )}

        {step === "phone" && (
          <TextField
            id="login-phone"
            inputRef={phoneRef}
            label="Mobile number"
            variant="outlined"
            type="tel"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(digitsOnly(e.target.value).slice(0, 10))}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            helperText="10-digit number, no spaces"
            sx={fieldSx}
            InputProps={fieldInputProps(<DialpadIcon sx={iconSx} />)}
          />
        )}

        {step === "phoneOtp" && (
          <TextField
            id="login-mobile-otp"
            inputRef={mobileOtpRef}
            label="One-time code"
            variant="outlined"
            fullWidth
            value={mobileOtp}
            onChange={(e) => setMobileOtp(digitsOnly(e.target.value).slice(0, OTP_LENGTH))}
            autoComplete="one-time-code"
            inputProps={{
              inputMode: "numeric",
              maxLength: OTP_LENGTH,
              style: { letterSpacing: "0.35em", fontSize: "1.15rem" },
            }}
            sx={fieldSx}
            InputProps={fieldInputProps(<VerifiedUserIcon sx={iconSx} />)}
          />
        )}

        {step === "otp" && (
          <TextField
            id="login-otp"
            inputRef={otpRef}
            label="One-time code"
            variant="outlined"
            fullWidth
            value={otp}
            onChange={(e) => setOtp(digitsOnly(e.target.value).slice(0, OTP_LENGTH))}
            autoComplete="one-time-code"
            inputProps={{
              inputMode: "numeric",
              maxLength: OTP_LENGTH,
              style: { letterSpacing: "0.35em", fontSize: "1.15rem" },
            }}
            sx={fieldSx}
            InputProps={fieldInputProps(<VerifiedUserIcon sx={iconSx} />)}
          />
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
          endIcon={submitting ? null : <ArrowForward sx={{ fontSize: 18 }} />}
          sx={submitButtonSx}
        >
          {submitting ? copy.pending : copy.action}
        </Button>

        {(step === "otp" || step === "phoneOtp") && (
          <Button
            type="button"
            color="inherit"
            fullWidth
            disabled={submitting || resendIn > 0}
            onClick={handleResend}
            sx={{ mt: 1, ...ghostButtonSx }}
          >
            {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
          </Button>
        )}

        {step !== "credentials" && (
          <Button
            type="button"
            color="inherit"
            fullWidth
            onClick={resetToCredentials}
            sx={{ mt: 0.5, ...ghostButtonSx }}
          >
            Back to sign in
          </Button>
        )}
      </form>
      <Typography
        sx={{
          display: "block",
          mt: 2.5,
          textAlign: "center",
          fontSize: 14,
          lineHeight: 1.5,
          color: loginColors.muted,
        }}
      >
        Need help?{" "}
        <Box component="span" sx={{ color: loginColors.teal }}>
          Contact IT Support
        </Box>
      </Typography>
    </AuthLayout>
  );
};

export default LoginForm;
